import { Box, Button, Container, Typography } from '@mui/material'
import {
    CardGiftcardOutlined,
    GroupAddOutlined,
    AccountBalanceWalletOutlined,
    VerifiedOutlined,
} from '@mui/icons-material'
import { useInView } from '../../hooks/useInView'

const PRIMARY = '#8c7c0c'
const PRIMARY_DARK = '#6b5e09'

const STEPS = [
    {
        icon: <GroupAddOutlined sx={{ fontSize: 28, color: '#fff' }} />,
        title: 'Invite a Landlord',
        desc: 'Share your unique referral code or link with a landlord you know.',
    },
    {
        icon: <VerifiedOutlined sx={{ fontSize: 28, color: '#fff' }} />,
        title: 'They Sign Up',
        desc: 'They register on Sheltergram using your code within 5 days of account creation.',
    },
    {
        icon: <AccountBalanceWalletOutlined sx={{ fontSize: 28, color: '#fff' }} />,
        title: 'You Earn 20%',
        desc: 'Earn 20% of Sheltergram\'s commission from your referral for up to 6 months.',
    },
]

const Referral = () => {
    const { ref, visible: inView } = useInView()

    return (
        <Box
            ref={ref}
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 50%, #c4aa14 100%)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Decorative blobs */}
            <Box sx={{ position: 'absolute', top: -60, right: -60, width: 320, height: 320, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.07)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 280, height: 280, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.05)', pointerEvents: 'none' }} />

            <Container maxWidth="lg">
                {/* Header */}
                <Box
                    className={inView ? 'sg-reveal' : ''}
                    sx={{ textAlign: 'center', mb: 7, opacity: 0, '&.sg-reveal': { opacity: 1 } }}
                >
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,.15)', borderRadius: 20, px: 2.5, py: 0.8, mb: 3 }}>
                        <CardGiftcardOutlined sx={{ fontSize: 18, color: '#fff' }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 1 }}>
                            REFERRAL PROGRAM
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 900, color: '#fff', lineHeight: 1.2, mb: 2 }}>
                        Earn While You Grow Your Network
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 15, md: 17 }, color: 'rgba(255,255,255,.8)', maxWidth: 520, mx: 'auto', lineHeight: 1.7 }}>
                        Invite landlords to Sheltergram and earn <strong style={{ color: '#fff' }}>20% of our commission</strong> from every property they manage — for up to 6 months.
                    </Typography>
                </Box>

                {/* Steps */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 3,
                        mb: 7,
                    }}
                >
                    {STEPS.map(({ icon, title, desc }, i) => (
                        <Box
                            key={title}
                            className={inView ? 'sg-reveal' : ''}
                            sx={{
                                opacity: 0,
                                '&.sg-reveal': { opacity: 1, transitionDelay: `${i * 120}ms` },
                                bgcolor: 'rgba(255,255,255,.12)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 4,
                                p: 3.5,
                                border: '1px solid rgba(255,255,255,.2)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                transition: 'transform .2s, box-shadow .2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 12px 40px rgba(0,0,0,.2)',
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{
                                    width: 52, height: 52, borderRadius: 3,
                                    bgcolor: 'rgba(255,255,255,.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    {icon}
                                </Box>
                                <Box sx={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Typography sx={{ fontWeight: 900, fontSize: 13, color: PRIMARY_DARK }}>{i + 1}</Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#fff' }}>{title}</Typography>
                            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,.8)', lineHeight: 1.6 }}>{desc}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Highlight strip */}
                <Box
                    className={inView ? 'sg-reveal' : ''}
                    sx={{
                        opacity: 0,
                        '&.sg-reveal': { opacity: 1, transitionDelay: '360ms' },
                        bgcolor: 'rgba(255,255,255,.95)',
                        borderRadius: 4,
                        p: { xs: 3, md: 4 },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'center' },
                        gap: 3,
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, md: 22 }, color: PRIMARY_DARK, mb: 0.5 }}>
                            Ready to start earning?
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
                            Create an account, find your referral code in your profile, and start sharing.
                            Referral codes are only valid for newly registered accounts within the first 5 days.
                        </Typography>
                    </Box>
                    <Button
                        href="/auth?signup=1"
                        variant="contained"
                        sx={{
                            bgcolor: PRIMARY, color: '#fff', borderRadius: 3,
                            px: 4, py: 1.5, fontWeight: 700, fontSize: 15,
                            textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0,
                            '&:hover': { bgcolor: PRIMARY_DARK },
                        }}
                    >
                        Get Started Free
                    </Button>
                </Box>
            </Container>
        </Box>
    )
}

export default Referral
